import { ClientScheduleCalendar } from '@apps/web/components/clients/client_schedule_calendar';
import { ProfileNotesPanel } from '@apps/web/components/entity_profile/profile_notes_panel';
import { Spinner } from '@apps/web/components/ui/spinner';
import { cn } from '@apps/web/lib/utils';
import { type OrgClientRead } from '@libraries/typescript/api_client';

type ClientOverviewProps = {
    readonly organizationId: string;
    readonly client: OrgClientRead;
    readonly timezone: string | null;
    readonly carePlanSummary: string;
    readonly carePlanBullets: string;
    readonly stacked?: boolean;
    readonly onShiftClick: (shiftId: string) => void;
};

/**
 * Landing section of the client popup: the three things schedulers
 * otherwise open the EHR for — this month's schedule, the care plan, and
 * notes — visible in one glance.
 */
export function ClientOverview({
    organizationId,
    client,
    timezone,
    carePlanSummary,
    carePlanBullets,
    stacked = false,
    onShiftClick,
}: ClientOverviewProps) {
    const hasCarePlanContent =
        carePlanSummary.trim() !== '' || carePlanBullets.trim() !== '';

    return (
        <div className="min-w-0">
            <h2 className="mb-3 flex min-h-8 items-center text-base font-semibold">
                Overview
            </h2>
            <div
                className={cn(
                    'grid items-start gap-5',
                    stacked
                        ? 'grid-cols-1'
                        : 'lg:grid-cols-[minmax(0,1.55fr)_minmax(280px,0.75fr)]',
                )}
            >
                <div className="min-w-0">
                    {timezone ? (
                        <ClientScheduleCalendar
                            organizationId={organizationId}
                            clientId={client.id}
                            timezone={timezone}
                            onShiftClick={onShiftClick}
                        />
                    ) : (
                        <div className="flex h-40 items-center justify-center">
                            <Spinner className="size-5" />
                        </div>
                    )}
                </div>
                <aside
                    className={cn('flex min-w-0 flex-col', stacked && 'pt-1')}
                >
                    <section className="border-b pb-4">
                        <h3 className="mb-2 text-sm font-semibold">
                            Care plan
                        </h3>
                        {hasCarePlanContent ? (
                            <div className="max-h-52 space-y-2 overflow-y-auto text-sm">
                                {carePlanSummary.trim() !== '' && (
                                    <p className="whitespace-pre-line">
                                        {carePlanSummary}
                                    </p>
                                )}
                                {carePlanBullets.trim() !== '' && (
                                    <p className="text-muted-foreground whitespace-pre-line">
                                        {carePlanBullets}
                                    </p>
                                )}
                            </div>
                        ) : (
                            <p className="text-muted-foreground text-sm">
                                No care plan summary yet.
                            </p>
                        )}
                    </section>
                    <div className="pt-4">
                        <ProfileNotesPanel
                            organizationId={organizationId}
                            entityType="client"
                            entityId={client.id}
                            entityFirstName={
                                client.preferred_name ?? client.first_name
                            }
                        />
                    </div>
                </aside>
            </div>
        </div>
    );
}
